import { Message } from "@/store/zustand/supabaseStore";
import { useMemo } from "react";

/**
 * Creates a regex-safe version of a search query and returns regex for matching
 * @param searchQuery The search query to process
 * @returns Object containing escaped query and regex for matching
 */
export const createSearchRegex = (searchQuery: string) => {
  if (!searchQuery?.trim()) return null;

  // Escape special regex characters in the search query
  const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  // Create regex for matching the query
  const regex = new RegExp(escapedQuery, "gi");

  return { escapedQuery, regex };
};

/**
 * Counts the number of matches of a search query in a collection of messages
 * @param messages The messages to search through
 * @param searchQuery The search query to find
 * @returns The total number of matches found
 */
export const countSearchMatches = (
  messages: Message[],
  searchQuery: string,
): number => {
  if (!searchQuery?.trim() || !messages?.length) return 0;

  const regexInfo = createSearchRegex(searchQuery);
  if (!regexInfo) return 0;

  let totalMatches = 0;

  messages.forEach((message) => {
    if (!message?.content) return;

    const content = message.content;
    const matches = content.match(regexInfo.regex);

    if (matches) {
      totalMatches += matches.length;
    }
  });

  return totalMatches;
};

/**
 * Custom hook for highlighting search matches in text
 * @param text The text to highlight matches in
 * @param searchQuery The search query to highlight
 * @returns Array of text parts with matching parts marked
 */
export const useHighlightedText = (text: string, searchQuery?: string) => {
  return useMemo(() => {
    if (!searchQuery?.trim() || !text) return [text];

    const regexInfo = createSearchRegex(searchQuery);
    if (!regexInfo) return [text];

    try {
      // Split the text by the regex to separate matched parts
      const regex = new RegExp(`(${regexInfo.escapedQuery})`, "gi");
      const parts = text.split(regex);

      return parts.map((part) => ({
        text: part,
        isMatch: part.toLowerCase() === searchQuery.toLowerCase(),
      }));
    } catch (error) {
      console.error("Error in highlight function:", error);
      return [{ text, isMatch: false }];
    }
  }, [text, searchQuery]);
};
